import { Button } from "antd";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Header } from "../../components/Header";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { useGetData } from "../../Network/useGetQueryData";
import { UserRolesId } from "../enum";

interface MonthlyReport {
  totalRequests: number;
  completionRate: number;
  newMemberships: number;
  customerShare: number;
  coverageAmount: number;
}

export const Dashboard = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.user);
  const { firstName, lastName, role, roleId } = userInfo || {};
  const navigate = useNavigate();

  const isAdmin = roleId === UserRolesId.Admin;
  const isSponsor = roleId === UserRolesId.Sponsor;
  const showKpis = isAdmin || isSponsor;

  const { data: reportData } = useGetData<{ data: MonthlyReport }>({
    queryKey: ["dashboard-report"],
    url: "/report/monthly",
    options: { enabled: showKpis, queryKey: ["dashboard-report"] },
  });
  const report = reportData?.data;

  const { data: customersData } = useGetData<{ data: { count: number } }>({
    queryKey: ["dashboard-customers-count"],
    url: "/customer",
    params: { offset: 0, limit: 1 },
    options: { enabled: isAdmin, queryKey: ["dashboard-customers-count"] },
  });

  const { data: providersData } = useGetData<{ data: { count: number } }>({
    queryKey: ["dashboard-providers-count"],
    url: "/service-provider",
    params: { offset: 0, limit: 1 },
    options: { enabled: isAdmin, queryKey: ["dashboard-providers-count"] },
  });

  return (
    <>
      <Header title={t("dashboard.title")} />
      <div className="dashboard-wrapper">
        <p>
          {t("dashboard.welcome")},{firstName && <span> {firstName}</span>}{" "}
          {lastName && <span> {lastName}</span>} {t("dashboard.to")}
        </p>

        {showKpis && (
          <div className="dashboard-stats-grid">
            {isAdmin && (
              <div className="dashboard-stat-card">
                <h3>{customersData?.data?.count ?? "-"}</h3>
                <p>{t("dashboard.stats.totalCustomers")}</p>
              </div>
            )}
            {isAdmin && (
              <div className="dashboard-stat-card">
                <h3>{providersData?.data?.count ?? "-"}</h3>
                <p>{t("dashboard.stats.totalProviders")}</p>
              </div>
            )}
            <div className="dashboard-stat-card">
              <h3>{report?.totalRequests ?? "-"}</h3>
              <p>{t("dashboard.stats.requestsThisMonth")}</p>
            </div>
            <div className="dashboard-stat-card">
              <h3>{report ? `${report.completionRate}%` : "-"}</h3>
              <p>{t("dashboard.stats.completionRate")}</p>
            </div>
            <div className="dashboard-stat-card">
              <h3>{report?.newMemberships ?? "-"}</h3>
              <p>{t("dashboard.stats.newMemberships")}</p>
            </div>
            {isSponsor && (
              <div className="dashboard-stat-card">
                <h3>{report ? `${report.customerShare.toLocaleString()} ${t("reports.currency")}` : "-"}</h3>
                <p>{t("dashboard.stats.customerShare")}</p>
              </div>
            )}
          </div>
        )}

        <div className="dashboard-action">
          {userInfo && role?.name !== "Service Provider Admin" && (
            <Button type="primary" onClick={() => navigate("/providers")}>
              {t("dashboard.viewProviders")}
            </Button>
          )}
          <Button onClick={() => navigate("/requests")}>
            {t("dashboard.viewRequests")}
          </Button>
        </div>
      </div>
    </>
  );
};
